import { capture_supabase_error } from '$lib/utils/capture_supabase_error';

/**
 * Get active entitlements for current user
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @returns {Promise<string[]>}
 */
export async function get_user_entitlements(supabase) {
    let entitlements = [];
    const now_iso = new Date().toISOString();

    try {
        let { data, error } = await supabase
            .from('User_Entitlements')
            .select(`product_id`)
            .or(`expires_at.is.null,expires_at.gt.${now_iso}`)
            .order('granted_at', { ascending: false });

        if (error) {
            capture_supabase_error(error, {
                message: 'Supabase error getting user entitlements',
                operation: 'select',
                table: 'User_Entitlements',
                extra: {
                    query: 'select * from User_Entitlements where expires_at is null or in future'
                }
            });
            console.error(error);
            return [];
        }

        entitlements = data.map((e) => e.product_id) || [];
    } catch (error) {
        capture_supabase_error(error, {
            message: 'Error getting user entitlements',
            operation: 'select',
            table: 'User_Entitlements',
            extra: {
                query: 'select * from User_Entitlements where expires_at is null or in future'
            }
        });
        console.error(error);
    }

    return entitlements;
}

/**
 * Resolve which product_ids a teacher can access via their school subscription.
 * Teacher is assumed to belong to a single school.
 * Uses a single nested query to fetch all data efficiently.
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string | null} user_id
 * @returns {Promise<{
 *   school_id: string | null;
 *   role: 'teacher' | 'admin' | null;
 *   is_school_teacher: boolean;
 *   is_school_admin: boolean;
 *   product_ids: string[];
 * }>}
 */
export async function get_teacher_allowed_product_ids(supabase, user_id) {
    const fallback = {
        school_id: null,
        role: null,
        is_school_teacher: false,
        is_school_admin: false,
        product_ids: []
    };

    try {
        if (!user_id) return fallback;

        // Single nested query: School_Members → Schools → School_Subscriptions → Products → Subscription_Items
        const { data: member_data, error } = await supabase
            .from('School_Members')
            .select(
                `
                school_id,
                role,
                Schools!inner (
                    School_Subscriptions (
                        status,
                        Products!subscription_product_id (
                            Subscription_Items!subscription_product_id (
                                child_product_id
                            )
                        )
                    )
                )
            `
            )
            .eq('user_id', user_id)
            .in('role', ['teacher', 'admin'])
            .in('status', ['invited', 'active'])
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

        if (error) {
            capture_supabase_error(error, {
                message: 'Supabase error getting teacher product access',
                operation: 'select',
                table: 'School_Members',
                extra: {
                    query: 'nested School_Members -> Subscriptions -> Items'
                }
            });
            console.error(error);
            return fallback;
        }

        const school_id = member_data?.school_id || null;
        const role = member_data?.role || null;
        if (!school_id || !role) return fallback;

        const school = Array.isArray(member_data?.Schools)
            ? member_data.Schools[0] || null
            : member_data?.Schools || null;

        const sub_raw = school?.School_Subscriptions;
        const sub = Array.isArray(sub_raw)
            ? sub_raw[0] || null
            : sub_raw || null;
        if (!sub?.status || !['trialing', 'active'].includes(sub.status)) {
            return {
                school_id,
                role,
                is_school_teacher: true,
                is_school_admin: role === 'admin',
                product_ids: []
            };
        }

        const product_raw = sub?.Products;
        const product = Array.isArray(product_raw)
            ? product_raw[0] || null
            : product_raw || null;
        const items_raw = product?.Subscription_Items;
        const items = Array.isArray(items_raw)
            ? items_raw
            : items_raw
            ? [items_raw]
            : [];

        const product_ids = [
            ...new Set(
                items.map((item) => item.child_product_id).filter(Boolean)
            )
        ];

        return {
            school_id,
            role,
            is_school_teacher: true,
            is_school_admin: role === 'admin',
            product_ids
        };
    } catch (error) {
        capture_supabase_error(error, {
            message: 'Unhandled error resolving teacher product access',
            operation: 'select',
            table: 'School_Members'
        });
        console.error(error);
        return fallback;
    }
}

/**
 * Patch course access flags from entitlements + school access list.
 * @param {Course[]} courses
 * @param {string[]} entitlements
 * @param {string[]} teacher_allowed_product_ids
 * @returns {Course[]}
 */
export function patch_purchased_courses(
    courses = [],
    entitlements = [],
    teacher_allowed_product_ids = []
) {
    const entitlement_set = new Set(entitlements || []);
    const teacher_access_set = new Set(teacher_allowed_product_ids || []);

    return (courses || []).map((course) => {
        const has_teacher_access = teacher_access_set.has(course.id);
        return {
            ...course,
            has_teacher_access,
            purchased: entitlement_set.has(course.id) || has_teacher_access
        };
    });
}

/**
 * Patch worksheet access flags from entitlements + school access list.
 * @param {Worksheet[]} worksheets
 * @param {string[]} entitlements
 * @param {string[]} teacher_allowed_product_ids
 * @returns {Worksheet[]}
 */
export function patch_purchased_worksheets(
    worksheets = [],
    entitlements = [],
    teacher_allowed_product_ids = []
) {
    const entitlement_set = new Set(entitlements || []);
    const teacher_access_set = new Set(teacher_allowed_product_ids || []);

    return (worksheets || []).map((worksheet) => {
        const has_teacher_access = teacher_access_set.has(worksheet.id);
        return {
            ...worksheet,
            has_teacher_access,
            purchased: entitlement_set.has(worksheet.id) || has_teacher_access
        };
    });
}

export function patch_purchased_bundles(bundles, courses, worksheets) {
    return bundles.map((bundle) => {
        bundle.items = bundle.items.map((item) => {
            if (item.type === 'course') {
                item.purchased = courses.some(
                    (c) => c.id === item.id && c.purchased
                );
            } else if (item.type === 'worksheet') {
                item.purchased = worksheets.some(
                    (w) => w.id === item.id && w.purchased
                );
            }
            return item;
        });

        bundle.purchased = bundle.items.every((item) => item.purchased);
        return bundle;
    });
}
